/**
 * CandidateFilters Component
 *
 * Shared filter UI for candidates across all contexts.
 * Mirrors the LegislatorFilters layout: card wrapper, labeled dropdowns
 * in a grid, SearchField + Clear Filters button row.
 */

import React from 'react';
import SearchField from '../SearchField';
import FilterActions from '../FilterActions';
import {
  CandidateFilterState,
  CandidateFilterOptions,
  CandidateFiltersConfig,
  DEFAULT_FILTERS_CONFIG,
} from './types';
import { padDistrictNumber } from '../../utils/districtDisplay';

/**
 * Office display order for the District dropdown:
 * State House → State Senate → US House → US Senate, everything else last.
 */
const officeRank = (office?: string): number => {
  const o = (office || '').trim().toLowerCase();
  const compact = o.replace(/[.\s]/g, ''); // "U.S. House" -> "ushouse"
  if (o.includes('colorado house') || o === 'house') return 0;
  if (o.includes('colorado senate') || o === 'senate') return 1;
  if (compact === 'ushouse') return 2;
  if (compact === 'ussenate') return 3;
  return 99;
};

/** Numeric district value for sorting; non-numeric sorts last. */
const districtNumber = (district?: string): number => {
  const n = parseInt((district || '').replace(/\D/g, ''), 10);
  return Number.isFinite(n) ? n : Number.MAX_SAFE_INTEGER;
};

/**
 * Canonical district option label — 'CO HD-07' / 'CO SD-07' / 'US HD-07' —
 * byte-for-byte the format the Legislators filter shows (its labels come from
 * GET /legislators/filter-options, built the same way; ratified 2026-08-27).
 *
 * The server's candidate filter-options already send canonical labels as the
 * option values, so those pass through untouched. A bare numeric value (older
 * data paths) gets the prefix derived from the office and a two-digit pad;
 * anything else passes through as-is.
 */
export const districtOptionLabel = (district?: string, office?: string): string => {
  const value = (district || '').trim();
  if (!value) return '';
  if (!/^\d+$/.test(value)) return value;

  const padded = padDistrictNumber(value);
  const o = (office || '').toLowerCase();
  const isUs = /(^|[^a-z])u\.?s\.?([^a-z]|$)|congress|united states/.test(o);
  if (o.includes('senate') && !isUs) return `CO SD-${padded}`;
  if (o.includes('house')) return isUs ? `US HD-${padded}` : `CO HD-${padded}`;
  return padded;
};

interface CandidateFiltersProps {
  filters: CandidateFilterState;
  filterOptions?: CandidateFilterOptions;
  config?: CandidateFiltersConfig;
  onSearchChange: (query: string) => void;
  onFilterChange: (key: keyof CandidateFilterState, value: string) => void;
  onClear: () => void;
  hasActiveFilters: boolean;
  showCard?: boolean;
  className?: string;
  isLoadingOptions?: boolean;
}

const CandidateFilters: React.FC<CandidateFiltersProps> = ({
  filters,
  filterOptions,
  config = DEFAULT_FILTERS_CONFIG,
  onSearchChange,
  onFilterChange,
  onClear,
  hasActiveFilters: _hasActiveFilters,
  showCard = true,
  className = '',
  isLoadingOptions = false,
}) => {
  const {
    showSearch = true,
    showOffice = true,
    showParty = true,
    showDistrict = true,
    showElectionYear = true,
    showStatus = true,
    showClearButton = true,
    compact = false,
  } = config;

  const visibleDropdowns = [
    showOffice,
    showParty,
    showDistrict,
    showElectionYear,
    showStatus,
  ].filter(Boolean).length;

  const getGridCols = () => {
    if (compact) return 'grid-cols-2 lg:grid-cols-4';
    if (visibleDropdowns <= 2) return 'grid-cols-1 sm:grid-cols-2';
    if (visibleDropdowns <= 3) return 'grid-cols-1 sm:grid-cols-3';
    if (visibleDropdowns <= 4) return 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4';
    if (visibleDropdowns <= 5) return 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-5';
    return 'grid-cols-1 sm:grid-cols-3 lg:grid-cols-6';
  };

  // District dropdown: limit to the selected office (when one is chosen) and
  // order by chamber (State House → State Senate → US House → US Senate), then
  // by district number.
  const districtOptions = (filterOptions?.districts ?? [])
    .filter(d => filters.officeSought === 'All' || d.office === filters.officeSought)
    .slice()
    .sort((a, b) => {
      const rank = officeRank(a.office) - officeRank(b.office);
      if (rank !== 0) return rank;
      const num = districtNumber(a.district) - districtNumber(b.district);
      if (num !== 0) return num;
      return (a.district || '').localeCompare(b.district || '');
    });

  const content = (
    <div className={compact ? 'space-y-3' : 'space-y-4'}>
      {/* Row 1: Filter Dropdowns Grid */}
      {visibleDropdowns > 0 && (
        <div className={`grid ${getGridCols()} gap-4`}>
          {showOffice && (
            <div>
              <label className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-1">
                Office
              </label>
              <select
                value={filters.officeSought}
                onChange={(e) => {
                  onFilterChange('officeSought', e.target.value);
                  // A district from a different office shouldn't persist.
                  if (filters.district !== 'All districts') {
                    onFilterChange('district', 'All districts');
                  }
                }}
                className="input"
                disabled={isLoadingOptions}
              >
                <option value="All">All Offices</option>
                {filterOptions?.offices.map(office => (
                  <option key={office} value={office}>{office}</option>
                ))}
              </select>
            </div>
          )}

          {showParty && (
            <div>
              <label className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-1">
                Party
              </label>
              <select
                value={filters.party}
                onChange={(e) => onFilterChange('party', e.target.value)}
                className="input"
                disabled={isLoadingOptions}
              >
                <option value="All parties">All Parties</option>
                {filterOptions?.parties.map(party => (
                  <option key={party} value={party}>{party}</option>
                ))}
              </select>
            </div>
          )}

          {showElectionYear && (
            <div>
              <label className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-1">
                Election Year
              </label>
              <select
                value={filters.electionYear}
                onChange={(e) => onFilterChange('electionYear', e.target.value)}
                className="input"
                disabled={isLoadingOptions}
              >
                <option value="All">All Years</option>
                {filterOptions?.electionYears.map(year => (
                  <option key={year} value={year.toString()}>{year}</option>
                ))}
              </select>
            </div>
          )}

          {showStatus && (
            <div>
              <label className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-1">
                Status
              </label>
              <select
                value={filters.status}
                onChange={(e) => onFilterChange('status', e.target.value)}
                className="input"
                disabled={isLoadingOptions}
              >
                <option value="All">All Statuses</option>
                {filterOptions?.statuses.map(status => (
                  <option key={status} value={status}>
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* District sits last, and its label matches the Legislators filter
              exactly — see districtOptionLabel. */}
          {showDistrict && (
            <div>
              <label className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-1">
                District
              </label>
              <select
                value={filters.district}
                onChange={(e) => onFilterChange('district', e.target.value)}
                className="input"
                disabled={isLoadingOptions}
              >
                <option value="All districts">All Districts</option>
                {districtOptions.map(d => (
                  <option key={`${d.office}-${d.district}`} value={d.district}>
                    {districtOptionLabel(d.district, d.office)}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}

      {/* Row 2: Search Bar + Clear Filters (fixed 5-col layout to match Legislators) */}
      {(showSearch || showClearButton) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 items-end">
          <div className="col-span-full lg:col-span-4">
            {showSearch && (
              <SearchField
                value={filters.searchQuery}
                onChange={onSearchChange}
                placeholder="Search candidates by name or district..."
                className="w-full"
                size={compact ? 'sm' : 'md'}
              />
            )}
          </div>
          {showClearButton && (
            <div className="col-span-full lg:col-span-1">
              <FilterActions onClear={onClear} />
            </div>
          )}
        </div>
      )}
    </div>
  );

  if (showCard) {
    return (
      <div className={`card ${className}`}>
        <div className={compact ? 'p-4' : 'p-6'}>
          {content}
        </div>
      </div>
    );
  }

  return <div className={className}>{content}</div>;
};

export default CandidateFilters;
